import React, { useState, useRef } from 'react';
import { 
  Upload, 
  CheckCircle, 
  AlertCircle,
  FileText,
  X,
  Loader,
  CreditCard,
  Shield,
  Building,
  Banknote,
  Award,
  Info
} from 'lucide-react';
import { uploadClient, endpoints } from '../api';
import './DirectKYCUpload.css';

const DirectKYCUpload = ({ vendorId, onUploadComplete, templates = [], disabled }) => {
  const [documents, setDocuments] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadResults, setUploadResults] = useState({});
  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(false);
  const fileInputRefs = useRef({});

  const documentTypes = {
    pan: {
      label: 'PAN Card',
      icon: <CreditCard size={20} />,
      required: true,
      accept: '.jpg,.jpeg,.png,.pdf',
      acceptTypes: 'image/jpeg,image/jpg,image/png,application/pdf',
      maxSize: '2MB',
      description: 'Upload your PAN card document'
    },
    aadhaar: {
      label: 'Aadhaar Card',
      icon: <Shield size={20} />,
      required: true,
      accept: '.jpg,.jpeg,.png,.pdf',
      acceptTypes: 'image/jpeg,image/jpg,image/png,application/pdf',
      maxSize: '2MB',
      description: 'Upload your Aadhaar card (first 8 digits must be masked)'
    },
    gst: {
      label: 'GST Certificate',
      icon: <FileText size={20} />,
      required: true,
      accept: '.jpg,.jpeg,.png,.pdf',
      acceptTypes: 'image/jpeg,image/jpg,image/png,application/pdf',
      maxSize: '5MB',
      description: 'Upload your GST registration certificate'
    },
    bankProof: {
      label: 'Bank Account Proof',
      icon: <Banknote size={20} />,
      required: true,
      accept: '.jpg,.jpeg,.png,.pdf',
      acceptTypes: 'image/jpeg,image/jpg,image/png,application/pdf',
      maxSize: '5MB',
      description: 'Upload bank statement or cancelled cheque'
    },
    shopLicense: {
      label: 'Shop/Trade License',
      icon: <Building size={20} />,
      required: true,
      accept: '.jpg,.jpeg,.png,.pdf',
      acceptTypes: 'image/jpeg,image/jpg,image/png,application/pdf',
      maxSize: '5MB',
      description: 'Upload your business license or trade certificate'
    },
    fssai: {
      label: 'FSSAI License',
      icon: <Award size={20} />,
      required: false,
      accept: '.jpg,.jpeg,.png,.pdf',
      acceptTypes: 'image/jpeg,image/jpg,image/png,application/pdf',
      maxSize: '5MB',
      description: 'Upload FSSAI license (optional - for food businesses)'
    }
  };

  const handleFileSelect = (docType) => {
    if (!fileInputRefs.current[docType]) {
      fileInputRefs.current[docType] = document.createElement('input');
      fileInputRefs.current[docType].type = 'file';
      fileInputRefs.current[docType].accept = documentTypes[docType].acceptTypes;
      fileInputRefs.current[docType].onchange = (e) => handleFileChange(e, docType);
    }
    fileInputRefs.current[docType].click();
  };

  const handleFileChange = (event, docType) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file
      const docType_info = documentTypes[docType];
      const maxSizeBytes = parseInt(docType_info.maxSize) * 1024 * 1024;
      
      if (file.size > maxSizeBytes) {
        setErrors(prev => ({
          ...prev,
          [docType]: `File size must be less than ${docType_info.maxSize}`
        }));
        return;
      }

      setDocuments(prev => ({
        ...prev,
        [docType]: file
      }));
      
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[docType];
        return newErrors;
      });
    }
  };

  const removeFile = (docType) => {
    setDocuments(prev => {
      const newDocs = { ...prev };
      delete newDocs[docType];
      return newDocs;
    });
    setUploadResults(prev => {
      const newResults = { ...prev };
      delete newResults[docType];
      return newResults;
    });
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[docType];
      return newErrors;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Check required documents
    Object.keys(documentTypes).forEach(docType => {
      if (documentTypes[docType].required && !documents[docType]) {
        newErrors[docType] = 'This document is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const uploadFiles = async () => {
    if (!validateForm()) return;
    
    setUploading(true);
    setUploadProgress({});
    setUploadResults({});

    const formData = new FormData();
    
    Object.keys(documents).forEach(docType => {
      formData.append(docType, documents[docType]);
    });

    try {
      const response = await uploadClient.post(endpoints.kycUpload(vendorId), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress({ overall: percentCompleted });
        },
      });

      if (response.data.success) {
        Object.keys(documents).forEach(docType => {
          setUploadResults(prev => ({
            ...prev,
            [docType]: { success: true, message: 'Uploaded successfully' }
          }));
        });
        
        // Call completion callback
        if (onUploadComplete) {
          onUploadComplete({
            success: true,
            message: `${Object.keys(documents).length} documents uploaded successfully`,
            successCount: Object.keys(documents).length,
            data: response.data.data
          });
        }

        // Reset form after successful upload
        setTimeout(() => {
          setDocuments({});
          setUploadResults({});
          setShowForm(false);
        }, 2000);
      } else {
        setErrors({ general: response.data.message || 'Upload failed' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Upload failed. Please try again.';
      if (error.response) {
        if (error.response.status === 413) {
          errorMessage = 'File size too large. Please reduce file sizes.';
        } else if (error.response.status === 400) {
          errorMessage = error.response.data?.message || 'Invalid file format or data.';
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.';
      }
      setErrors({ general: errorMessage });
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (size) => {
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return Math.round(size / 1024) + ' KB';
    return Math.round(size / (1024 * 1024)) + ' MB';
  };

  const getTotalFiles = () => Object.keys(documents).length;
  const getRequiredFiles = () => Object.keys(documentTypes).filter(key => documentTypes[key].required).length;
  const getUploadedRequiredFiles = () => {
    return Object.keys(documentTypes).filter(key => 
      documentTypes[key].required && documents[key]
    ).length;
  };

  const canSubmit = () => {
    const requiredCount = getRequiredFiles();
    const uploadedRequiredCount = getUploadedRequiredFiles();
    return uploadedRequiredCount === requiredCount && !uploading;
  };

  return (
    <div className="direct-kyc-upload">
      {/* Upload Button */}
      <button 
        onClick={() => setShowForm(true)} 
        className="upload-documents-btn"
        disabled={disabled || uploading}
      >
        <Upload size={16} />
        Upload Documents
      </button>

      {/* Upload Form Modal */}
      {showForm && (
        <div className="upload-form-overlay">
          <div className="upload-form-container">
            <div className="form-header">
              <h2>Upload KYC Documents</h2>
              <button 
                onClick={() => setShowForm(false)} 
                className="close-form-btn"
                disabled={uploading}
              >
                <X size={20} />
              </button>
            </div>

            <div className="form-content">
              <div className="form-info">
                <Info size={16} />
                <span>
                  Upload all required documents to complete your KYC verification. 
                  Files must be clear and readable.
                </span>
              </div>

              {/* General Error */}
              {errors.general && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  <span>{errors.general}</span>
                </div>
              )}

              {/* Document Upload Fields */}
              <div className="document-fields">
                {Object.keys(documentTypes).map(docType => {
                  const docInfo = documentTypes[docType];
                  const hasFile = documents[docType];
                  const hasError = errors[docType];
                  const result = uploadResults[docType];

                  return (
                    <div key={docType} className={`document-field ${hasError ? 'error' : ''}`}>
                      <div className="field-header">
                        <div className="field-label">
                          {docInfo.icon}
                          <span className="label-text">
                            {docInfo.label}
                            {docInfo.required && <span className="required">*</span>}
                          </span>
                        </div>
                        <span className="field-info">Max: {docInfo.maxSize}</span>
                      </div>

                      <p className="field-description">{docInfo.description}</p>

                      {hasFile ? (
                        <div className="selected-file">
                          <div className="file-info">
                            <FileText size={16} />
                            <span className="file-name">{hasFile.name}</span>
                            <span className="file-size">({formatFileSize(hasFile.size)})</span>
                          </div>
                          <div className="file-actions">
                            {result?.success && (
                              <CheckCircle size={16} className="success-icon" />
                            )}
                            {!uploading && (
                              <button 
                                onClick={() => removeFile(docType)}
                                className="remove-file-btn"
                              >
                                <X size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <button 
                          onClick={() => handleFileSelect(docType)}
                          className="select-file-btn"
                          disabled={uploading}
                        >
                          <Upload size={16} />
                          Select {docInfo.label}
                        </button>
                      )}

                      {hasError && (
                        <div className="field-error">
                          <AlertCircle size={14} />
                          <span>{hasError}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="upload-progress">
                  <div className="progress-header">
                    <Loader size={16} className="spinning" />
                    <span>Uploading documents...</span>
                    <span className="progress-percent">{uploadProgress.overall || 0}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress.overall || 0}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div className="form-actions">
                <div className="files-summary">
                  <span>
                    {getTotalFiles()} of {getRequiredFiles()} required documents selected
                  </span>
                </div>
                <div className="action-buttons">
                  <button 
                    onClick={() => setShowForm(false)}
                    className="cancel-btn"
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={uploadFiles}
                    className="submit-btn"
                    disabled={!canSubmit()}
                  >
                    {uploading ? (
                      <>
                        <Loader size={16} className="spinning" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        Upload Documents
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectKYCUpload;